import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faArrowLeft,
  faCopy,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import { toast } from "sonner";

Modal.setAppElement("#root");

const SetupPrinter = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [credentialsModalIsOpen, setCredentialsModalIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const { t } = useTranslation();
  const { user } = useUser();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [message, setMessage] = useState("");
  const [credentials, setCredentials] = useState<any>(null);
  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  const openCredentialsModal = () => setCredentialsModalIsOpen(true);
  const closeCredentialsModal = () => {
    setCredentialsModalIsOpen(false);
    setCredentials(null);
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard!");
    });
  };

  const handleDownload = (platform: string) => {
    const url =
      platform === "windows"
        ? `${import.meta.env.VITE_APP_API_BASE_URL}print/download/windows/`
        : `${import.meta.env.VITE_APP_API_BASE_URL}print/download/osx/`;
    window.location.href = url;
    setStep(2);
    closeModal();
  };

  const handleSignUp = async () => {
    try {
      if (!axiosInstance || !user?.selectedBranch) {
        console.error("Axios instance or selected branch not available");
        return;
      }
      const response = await axiosInstance.post("print/childaccount/", {
        branch_id: user.selectedBranch.id,
      });
      setMessage("Successfully signed up for Redbird Print Account.");
      setStep(3);
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        setMessage(
          "This branch already has a Redbird Print Account. Please see your credentials in the next section.",
        );
        setStep(3);
      } else {
        console.error("Error signing up:", error);
        setMessage("An error occurred while signing up. Please try again.");
      }
    }
  };

  const handleRevealCredentials = async () => {
    try {
      if (!axiosInstance || !user?.selectedBranch) {
        console.error("Axios instance or selected branch not available");
        return;
      }

      const response = await axiosInstance.get(
        `print/childaccount/?branch_id=${user.selectedBranch.id}`,
      );
      if (response.status === 200) {
        setCredentials(response.data);
        setCredentialsModalIsOpen(true);
        setStep(4);
      }
    } catch (error) {
      console.error("Error fetching credentials:", error);
      setMessage(
        "An error occurred while fetching credentials. Please try again.",
      );
    }
  };

  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      padding: "0",
      border: "none",
      borderRadius: "0.5rem",
      maxWidth: "90vw",
      width: "800px",
      maxHeight: "90vh",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
    },
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/printer"
          className="rounded-md bg-gray-200 px-4 py-2 font-bold text-gray-700 shadow transition duration-150 ease-in-out hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Printers
        </Link>
        <h1 className="text-2xl font-bold">Printer Setup</h1>
      </div>
      <div className="mt-4 space-y-4">
        {step >= 1 && (
          <div className="card rounded-md border p-4 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">
              Step 1: Download Print Client
            </h2>
            <div className="flex flex-col items-center">
              <button
                onClick={openModal}
                className="mb-4 w-full rounded-md bg-red-500 px-4 py-2 font-bold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 md:w-64"
              >
                {t("back.management.printer.downloadClient")}
              </button>
              <h3 className="mb-2 mt-4 text-lg font-semibold">
                Already have Redbird Print Client?
              </h3>
              <button
                onClick={handleNextStep}
                className="w-full rounded-md bg-red-500 px-4 py-2 font-bold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 md:w-64"
              >
                Next Step
              </button>
            </div>
          </div>
        )}
        {step >= 2 && (
          <div className="card rounded-md border p-4 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">
              Step 2: Sign Up for Redbird Print Account
            </h2>
            <div className="flex justify-center">
              <button
                onClick={handleSignUp}
                className="mb-2 w-full rounded-md bg-red-500 px-4 py-2 font-bold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 md:w-64"
              >
                Sign Up
              </button>
            </div>
            {message && <p className="mt-2 text-sm text-gray-600">{message}</p>}
          </div>
        )}
        {step >= 3 && (
          <div className="card rounded-md border p-4 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">
              Step 3: Reveal Your Credentials
            </h2>
            <div className="flex justify-center">
              <button
                onClick={handleRevealCredentials}
                className="mb-2 w-full rounded-md bg-red-500 px-4 py-2 font-bold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 md:w-64"
              >
                Reveal Credentials
              </button>
            </div>
          </div>
        )}
        {step >= 4 && (
          <div className="card rounded-md border p-4 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">
              Step 4: Double Check Previous Steps
            </h2>
            <p>Ensure you have completed all previous steps correctly.</p>
          </div>
        )}
      </div>

      {/* Download Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Download Print Client"
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <h2 className="mb-4 text-xl font-semibold">Download Print Client</h2>
        <div className="flex flex-col items-center">
          <div className="w-full max-w-xs">
            <button
              onClick={() => handleDownload("windows")}
              className="mb-2 w-full rounded-md bg-red-500 px-4 py-2 font-bold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              Download for Windows
            </button>
            <button
              onClick={() => handleDownload("osx")}
              className="mb-4 w-full rounded-md bg-red-500 px-4 py-2 font-bold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              Download for macOS
            </button>
          </div>
          <button
            onClick={closeModal}
            className="rounded-md bg-gray-200 px-4 py-2 font-bold shadow hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
          >
            <FontAwesomeIcon icon={faTimes} className="mr-2" />
            Close
          </button>
        </div>
      </Modal>

      {/* Credentials Modal */}
      <Modal
        isOpen={credentialsModalIsOpen}
        onRequestClose={closeCredentialsModal}
        contentLabel="Your Credentials"
        style={customModalStyles}
      >
        <div className="max-h-[calc(90vh-2rem)] overflow-y-auto p-6">
          <h2 className="mb-6 text-2xl font-bold text-gray-800">
            Your Credentials
          </h2>
          <div className="flex flex-col items-center">
            {credentials && (
              <div className="mb-6 w-full">
                <div className="overflow-hidden rounded-lg bg-white shadow-md">
                  <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                      Account Information
                    </h3>
                  </div>
                  <div className="space-y-4 px-6 py-4">
                    {Object.entries(credentials).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm font-medium capitalize text-gray-600">
                          {key.replace(/_/g, " ")}:
                        </span>
                        <div className="flex items-center">
                          <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-800">
                            {String(value)}
                          </span>
                          <button
                            onClick={() => copyToClipboard(String(value))}
                            className="ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            title="Copy to clipboard"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={closeCredentialsModal}
              className="rounded-md bg-red-500 px-6 py-2 font-bold text-white shadow transition duration-150 ease-in-out hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SetupPrinter;
